import { useRef, useState } from 'react'
import { observer } from 'mobx-react-lite'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Paperclip, SendHorizontal, Loader2 } from 'lucide-react'

interface MessageInputProps {
  disabled: boolean
  disabledReason?: string
  sending: boolean
  uploading: boolean
  onSend: (text: string) => void
  onImageSelected: (file: File) => void
}

const MessageInput = observer(({ disabled, disabledReason, sending, uploading, onSend, onImageSelected }: MessageInputProps) => {
  const [text, setText] = useState('')
  const fileInputRef = useRef<HTMLInputElement>(null)

  const handleSubmit = () => {
    const trimmed = text.trim()
    if (!trimmed || disabled) return
    onSend(trimmed)
    setText('')
  }

  return (
    <div className="border-t p-3 space-y-1">
      {disabled && disabledReason && (
        <div className="text-xs text-muted-foreground px-1">{disabledReason}</div>
      )}
      <div className="flex items-center gap-2">
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={(e) => {
            const file = e.target.files?.[0]
            if (file) {
              onImageSelected(file)
            }
            e.target.value = ''
          }}
        />
        <Button
          type="button"
          variant="ghost"
          size="icon"
          aria-label="画像を添付"
          disabled={disabled || uploading}
          onClick={() => fileInputRef.current?.click()}
        >
          {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Paperclip className="h-4 w-4" />}
        </Button>

        <Input
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.nativeEvent.isComposing && !e.shiftKey) {
              e.preventDefault()
              handleSubmit()
            }
          }}
          placeholder={disabled ? 'メッセージを送信するには対応を開始してください' : 'メッセージを入力 (Enter で送信)'}
          disabled={disabled}
        />

        <Button
          type="button"
          size="sm"
          onClick={handleSubmit}
          disabled={disabled || !text.trim() || sending}
          aria-label="送信"
        >
          {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <SendHorizontal className="h-4 w-4" />}
        </Button>
      </div>
    </div>
  )
})

export default MessageInput